"use client";

import { useTranslations } from "next-intl";
import { trpc } from "@/lib/trpc-client";
import { looksLikeHttpUrl } from "./applyForm";
import { LinkPreviewRow, PreviewMessage } from "./LinkPreviewRow";
import { useSettledValue } from "./useSettledValue";

/**
 * What the portfolio link leads to, under its input: the page's title and
 * image, or, for a TikTok or Instagram link, the profile it opens, drawn the
 * same way the handle fields draw one.
 *
 * Advisory, like the handle previews, and quieter than them: a portfolio link
 * is never the reason for a rejection, and a site that refuses our server
 * (Canva does) still opens for a person, so "no preview" is worded as a
 * reminder rather than as a fault. Only a TikTok or Instagram profile the
 * server is sure doesn't exist is a warning.
 *
 * Nothing is asked of the server until the link could be one: a half-typed
 * `https://jan` is not worth a request, and the field's own error covers it.
 */
export function PortfolioLinkPreview({ url }: { url: string }) {
  const t = useTranslations("apply.fields.portfolioPreview");
  const tProfile = useTranslations("apply.fields.profilePreview");
  const typed = url.trim();
  const settled = useSettledValue(typed);
  const lookable = looksLikeHttpUrl(settled);
  const query = trpc.creatorApplication.previewPortfolio.useQuery(
    { url: settled },
    {
      enabled: lookable,
      retry: false,
      staleTime: 10 * 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (!looksLikeHttpUrl(typed)) return null;
  if (settled !== typed || query.isFetching) {
    return <PreviewMessage>{t("looking")}</PreviewMessage>;
  }

  const data = query.data;
  if (!data || data.status === "unknown") {
    return <PreviewMessage>{t("unknown")}</PreviewMessage>;
  }
  if (data.status === "profile_not_found") {
    return (
      <PreviewMessage warn>
        {t("profileNotFound", {
          handle: data.handle,
          platform: data.platform === "tiktok" ? "TikTok" : "Instagram",
        })}
      </PreviewMessage>
    );
  }
  if (data.status === "profile") {
    return (
      <LinkPreviewRow
        imageUrl={data.avatarUrl}
        imageAlt={tProfile("avatarAlt", { handle: data.handle })}
        imageShape="person"
        label={tProfile("applyingAs")}
        value={data.name ?? `@${data.handle}`}
      />
    );
  }
  return (
    <LinkPreviewRow
      imageUrl={data.imageUrl}
      imageAlt={t("imageAlt", { site: data.siteName })}
      imageShape="page"
      labelIconUrl={data.faviconUrl}
      label={data.siteName}
      value={data.title ?? data.siteName}
      clamp
    />
  );
}
